import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Edit3, Save, X, Copy, Sparkles, MessagesSquare, Archive as ArchiveIcon, RotateCcw, ShieldOff, Link as LinkIcon, ExternalLink } from 'lucide-react';
import ProfileShell, { DetailField, ProseBlock } from './ProfileShell';
import InviteStatusBadge from './InviteStatusBadge';
import { inviteApi, AUDIENCE_TYPES, PRIORITIES } from '../../lib/inviteApi';

const EDITABLE = [
  'name', 'organization_name', 'role_title', 'category', 'email',
  'website', 'instagram', 'linkedin', 'x_url', 'city', 'country', 'region',
  'short_description', 'bio', 'why_relevant', 'internal_note',
];

export default function ContactProfile({ contactId, exhibitionId, onClose, onChanged, onOpenThread }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [linkDraft, setLinkDraft] = useState({});

  const load = async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const d = await inviteApi.getContact(contactId);
      setDetail(d);
      setDraft({ ...d.contact });
    } catch (err) { toast.error(errorDetail(err, 'Failed to load')); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contactId]);

  // The exhibition_link that maps THIS contact to the current exhibition.
  const link = (detail?.exhibition_links || []).find((l) => l.exhibition_id === exhibitionId) || detail?.exhibition_links?.[0] || null;
  const thread = (detail?.threads || []).find((t) => t.exhibition_id === exhibitionId) || detail?.threads?.[0] || null;
  const c = (editing ? draft : detail?.contact) || {};
  const tickets = detail?.tickets || [];

  useEffect(() => { if (link) setLinkDraft({ ...link }); }, [link]);

  const update = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const handleSave = async () => {
    if (busy) return; setBusy(true);
    const patch = {};
    for (const k of EDITABLE) {
      if ((draft[k] ?? '') !== (detail.contact[k] ?? '')) patch[k] = draft[k] ?? null;
    }
    try {
      if (Object.keys(patch).length) {
        await inviteApi.patchContact(contactId, patch);
        toast.success('Saved.');
      }
      // Link priority + relationship_status changes
      if (link) {
        const linkPatch = {};
        for (const k of ['priority', 'relationship_status', 'audience_type']) {
          if ((linkDraft[k] ?? '') !== (link[k] ?? '')) linkPatch[k] = linkDraft[k] ?? null;
        }
        if (Object.keys(linkPatch).length) await inviteApi.patchLink(link.id, linkPatch);
      }
      setEditing(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(errorDetail(err, 'Save failed'));
    } finally { setBusy(false); }
  };

  const generate = async () => {
    if (!thread) { toast.error('No thread on this contact for the current exhibition.'); return; }
    setBusy(true);
    try { await inviteApi.generateOutreach(thread.id); toast.success('Drafts created.'); await load(); }
    catch (err) { toast.error(errorDetail(err, 'Generate failed')); }
    finally { setBusy(false); }
  };
  const archive = async () => {
    setBusy(true);
    try { await inviteApi.archiveContact(contactId); toast.success('Archived.'); onChanged?.(); onClose?.(); }
    catch (err) { toast.error(errorDetail(err, 'Archive failed')); }
    finally { setBusy(false); }
  };
  const restore = async () => {
    setBusy(true);
    try { await inviteApi.restoreContact(contactId); toast.success('Restored.'); onChanged?.(); await load(); }
    catch (err) { toast.error(errorDetail(err, 'Restore failed')); }
    finally { setBusy(false); }
  };
  const suppress = async () => {
    if (!c.email) { toast.error('No email — cannot suppress.'); return; }
    setBusy(true);
    try {
      await inviteApi.addSuppression({ email: c.email, reason: 'do_not_contact', source_contact_id: contactId, source_thread_id: thread?.id });
      toast.success('Added to suppression.');
      onChanged?.(); await load();
    } catch (err) { toast.error(errorDetail(err, 'Suppression failed')); }
    finally { setBusy(false); }
  };
  const copyTicketUrl = async () => {
    if (!thread) { toast.error('No thread on this contact.'); return; }
    try {
      const r = await inviteApi.getTicketUrl(thread.id);
      await navigator.clipboard.writeText(r.url);
      toast.success('Tracking URL copied');
    } catch (err) { toast.error(errorDetail(err, 'Failed')); }
  };

  return (
    <ProfileShell onClose={onClose} testid="invite-contact-profile">
      {/* Header */}
      <div className="px-6 py-5 border-b border-[#1a1a1a]">
        <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#90E0EF]">Invite Contact</p>
        {editing ? (
          <input value={draft.name || ''} onChange={update('name')} className="invite-input w-full mt-1 text-lg font-outfit text-white !p-1" />
        ) : (
          <h2 className="font-outfit text-xl text-white tracking-tight mt-0.5">{c.name || '—'}</h2>
        )}
        <p className="text-xs font-mono text-[#999] mt-0.5">
          {c.role_title || ''}{c.role_title && c.organization_name ? ' · ' : ''}{c.organization_name || ''}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {c.category && <InviteStatusBadge status={c.category} />}
          {link?.priority && <InviteStatusBadge status={link.priority} />}
          {link?.relationship_status && <InviteStatusBadge status={link.relationship_status} />}
          {c.global_status && c.global_status !== 'active' && <InviteStatusBadge status={c.global_status} strong />}
          {thread?.status && <InviteStatusBadge status={`thread: ${thread.status.replace(/_/g, ' ')}`} />}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-3 gap-6 px-6 py-6">
        <div className="lg:col-span-2 space-y-6">
          {loading && <p className="text-xs font-mono text-[#555]">Loading…</p>}
          {!loading && !editing && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <DetailField label="Email" value={c.email} mono />
              <DetailField label="Confidence" value={c.email_confidence} />
              <DetailField label="City" value={c.city} />
              <DetailField label="Country" value={c.country} />
              <DetailField label="Region" value={c.region} />
              <DetailField label="Website" value={c.website} link mono />
              <DetailField label="Instagram" value={c.instagram} link mono />
              <DetailField label="LinkedIn" value={c.linkedin} link mono />
              <DetailField label="X / Twitter" value={c.x_url} link mono />
              <DetailField label="Source URL" value={c.source_url} link mono />
              <DetailField label="Source type" value={c.source_type} />
              <DetailField label="Quality score" value={c.contact_quality_score} />
            </div>
          )}

          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <EditField label="Name" value={draft.name} onChange={update('name')} />
              <EditSelect label="Category" value={draft.category} onChange={update('category')} options={AUDIENCE_TYPES} />
              <EditField label="Organization" value={draft.organization_name} onChange={update('organization_name')} />
              <EditField label="Role title" value={draft.role_title} onChange={update('role_title')} />
              <EditField label="Email" value={draft.email} onChange={update('email')} />
              <EditField label="City" value={draft.city} onChange={update('city')} />
              <EditField label="Country" value={draft.country} onChange={update('country')} />
              <EditField label="Region" value={draft.region} onChange={update('region')} />
              <EditField label="Website" value={draft.website} onChange={update('website')} />
              <EditField label="Instagram" value={draft.instagram} onChange={update('instagram')} />
              <EditField label="LinkedIn" value={draft.linkedin} onChange={update('linkedin')} />
              <EditField label="X / Twitter" value={draft.x_url} onChange={update('x_url')} />
              {link && <EditSelect label="Relationship" value={linkDraft.relationship_status} onChange={(e) => setLinkDraft((l) => ({ ...l, relationship_status: e.target.value }))} options={LINK_STATUSES} />}
              {link && <EditSelect label="Priority" value={linkDraft.priority} onChange={(e) => setLinkDraft((l) => ({ ...l, priority: e.target.value }))} options={PRIORITIES} />}
            </div>
          )}

          {!editing && (
            <>
              <ProseBlock label="Description">{c.short_description}</ProseBlock>
              <ProseBlock label="Bio">{c.bio}</ProseBlock>
              <ProseBlock label="Why relevant">{c.why_relevant}</ProseBlock>
              <ProseBlock label="Internal note">{c.internal_note}</ProseBlock>
            </>
          )}
          {editing && (
            <div className="space-y-3">
              <EditTextarea label="Description" value={draft.short_description} onChange={update('short_description')} />
              <EditTextarea label="Bio" value={draft.bio} onChange={update('bio')} />
              <EditTextarea label="Why relevant" value={draft.why_relevant} onChange={update('why_relevant')} />
              <EditTextarea label="Internal note" value={draft.internal_note} onChange={update('internal_note')} />
            </div>
          )}

          {/* Outreach */}
          {(thread || link) && (() => {
            const fields = [
              thread?.last_outbound_at && { label: 'Last outbound', value: new Date(thread.last_outbound_at).toLocaleString() },
              thread?.last_inbound_at && { label: 'Last inbound', value: new Date(thread.last_inbound_at).toLocaleString() },
              (thread?.followup_2_due_at || thread?.followup_1_due_at) && { label: 'Next follow-up', value: new Date(thread.followup_2_due_at || thread.followup_1_due_at).toLocaleString() },
              thread?.current_step != null && { label: 'Current step', value: String(thread.current_step) },
              thread?.agent_mode && { label: 'Agent mode', value: thread.agent_mode, badge: true },
              thread?.status && { label: 'Thread status', value: thread.status, badge: true },
              link?.relationship_status && { label: 'Relationship', value: link.relationship_status, badge: true },
              link?.priority && { label: 'Priority', value: link.priority, badge: true },
            ].filter(Boolean);
            if (!fields.length) return null;
            return (
              <section className="space-y-2">
                <h4 className="text-[10px] font-mono text-[#90E0EF] tracking-[0.22em] uppercase">Outreach</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {fields.map((f, i) => <DetailField key={i} label={f.label} value={f.value} />)}
                </div>
              </section>
            );
          })()}

          {/* Source / origin */}
          {(c.source_type || c.source_url || c.source_notes) && (
            <section className="space-y-2">
              <h4 className="text-[10px] font-mono text-[#90E0EF] tracking-[0.22em] uppercase">Source</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <DetailField label="Source type" value={c.source_type} />
                <DetailField label="Source URL" value={c.source_url} link mono />
              </div>
              <ProseBlock label="Source notes">{c.source_notes}</ProseBlock>
            </section>
          )}

          {/* Tags */}
          {Array.isArray(c.tags) && c.tags.length > 0 && (
            <section className="space-y-2">
              <h4 className="text-[10px] font-mono text-[#90E0EF] tracking-[0.22em] uppercase">Tags</h4>
              <div className="flex flex-wrap gap-1.5">
                {c.tags.map((t, i) => (
                  <span key={i} className="text-[10px] font-mono text-[#aaa] px-2 py-0.5 border border-[#222] tracking-[0.12em]">{String(t)}</span>
                ))}
              </div>
            </section>
          )}

          {/* Tickets */}
          {tickets.length > 0 && (
            <section className="space-y-2">
              <h4 className="text-[10px] font-mono text-[#90E0EF] tracking-[0.22em] uppercase">Linked tickets</h4>
              <div className="space-y-1.5">
                {tickets.map((t) => (
                  <div key={t.id} className="border border-[#1a1a1a] px-3 py-2 text-[11px] font-mono flex items-center justify-between">
                    <span className="text-[#aaa]">{t.email}</span>
                    <InviteStatusBadge status={t.decision_status || t.status || 'pending'} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Messages summary */}
          {!!detail?.messages?.length && (
            <section className="space-y-2">
              <h4 className="text-[10px] font-mono text-[#90E0EF] tracking-[0.22em] uppercase">Recent messages</h4>
              <div className="space-y-1.5">
                {detail.messages.slice(0, 5).map((m) => (
                  <div key={m.id} className="border border-[#1a1a1a] px-3 py-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      <InviteStatusBadge status={m.direction} />
                      <InviteStatusBadge status={m.message_type} />
                      <InviteStatusBadge status={m.status} />
                    </div>
                    <p className="text-[11px] font-mono text-[#ccc] truncate">{m.subject || '(no subject)'}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Action rail */}
        <aside className="lg:col-span-1 space-y-3">
          <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-4 space-y-2.5">
            <p className="text-[10px] font-mono text-[#777] tracking-[0.22em] uppercase mb-1">Actions</p>

            {!editing && (
              <>
                <ActionBtn icon={Sparkles} label="Generate outreach" onClick={generate} tone="cyan" disabled={!thread} testid="contact-profile-generate" />
                {thread && (
                  <ActionBtn icon={MessagesSquare} label="Open thread" onClick={() => onOpenThread?.(thread.id)} tone="cyan" testid="contact-profile-open-thread" />
                )}
                <ActionBtn icon={LinkIcon} label="Copy invite URL" onClick={copyTicketUrl} tone="muted" testid="contact-profile-copy-url" disabled={!thread} />
                <ActionBtn icon={Edit3} label="Edit" onClick={() => setEditing(true)} tone="muted" testid="contact-profile-edit" />
                <ActionBtn icon={ShieldOff} label="Add suppression" onClick={suppress} tone="rose" disabled={!c.email} testid="contact-profile-suppress" />
                {c.global_status !== 'archived' ? (
                  <ActionBtn icon={ArchiveIcon} label="Archive" onClick={archive} tone="muted" testid="contact-profile-archive" />
                ) : (
                  <ActionBtn icon={RotateCcw} label="Restore" onClick={restore} tone="cyan" testid="contact-profile-restore" />
                )}
                <ActionBtn
                  icon={Copy}
                  label="Copy email"
                  onClick={async () => {
                    if (!c.email) { toast.error('No email.'); return; }
                    try { await navigator.clipboard.writeText(c.email); toast.success('Email copied'); } catch { /* */ }
                  }}
                  tone="muted"
                  disabled={!c.email}
                />
                {c.source_url && (
                  <ActionBtn icon={ExternalLink} label="Open source" onClick={() => window.open(c.source_url, '_blank')} tone="muted" />
                )}
              </>
            )}
            {editing && (
              <>
                <ActionBtn icon={Save} label={busy ? 'Saving…' : 'Save edits'} onClick={handleSave} tone="cyan" testid="contact-profile-save" />
                <ActionBtn icon={X} label="Cancel" onClick={() => { setDraft({ ...detail.contact }); setLinkDraft(link ? { ...link } : {}); setEditing(false); }} tone="muted" />
              </>
            )}
          </div>
        </aside>
      </div>
    </ProfileShell>
  );
}

const LINK_STATUSES = [
  'prospect', 'saved', 'invited', 'sent', 'delivered', 'opened', 'clicked',
  'replied', 'interested', 'followup_due', 'ticket_requested', 'ticket_accepted',
  'attended', 'declined', 'cold', 'paused', 'bounced', 'unsubscribed',
  'removed', 'archived',
];

function EditField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">{label}</span>
      <input className="invite-input w-full" value={value ?? ''} onChange={onChange} />
    </label>
  );
}
function EditSelect({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">{label}</span>
      <select className="invite-select w-full" value={value ?? ''} onChange={onChange}>
        <option value="">—</option>
        {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
      </select>
    </label>
  );
}
function EditTextarea({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">{label}</span>
      <textarea rows={3} className="invite-input w-full" value={value ?? ''} onChange={onChange} />
    </label>
  );
}

function ActionBtn({ icon: Icon, label, onClick, tone = 'cyan', testid, disabled = false }) {
  const map = {
    cyan: 'border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10',
    rose: 'border-[rgba(232,139,160,0.4)] text-[#e88ba0] hover:bg-[rgba(232,139,160,0.06)]',
    muted: 'border-[#333] text-[#aaa] hover:text-white hover:border-[#555]',
  };
  return (
    <button
      onClick={onClick}
      data-clickable="true"
      data-testid={testid}
      disabled={disabled}
      className={`w-full inline-flex items-center gap-2 text-[10px] font-mono px-3 py-2 border uppercase tracking-[0.14em] transition-colors ${
        disabled ? 'opacity-40 cursor-not-allowed border-[#222] text-[#666]' : map[tone]
      }`}
    >
      <Icon className="w-3 h-3"/> {label}
    </button>
  );
}

function errorDetail(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return d.detail || d.message || fallback;
  return err?.message || fallback;
}
